import Seo from "@/components/Seo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Hero from "@/components/landing/Hero";
import StatsBar from "@/components/landing/StatsBar";
import Services from "@/components/landing/Services";
import Genres from "@/components/landing/Genres";
import MixPlayer from "@/components/landing/MixPlayer";
import About from "@/components/landing/About";
import Gallery from "@/components/landing/Gallery";
import VideoSection from "@/components/landing/VideoSection";
import Testimonials from "@/components/landing/Testimonials";
import CaseStudies from "@/components/landing/CaseStudies";
import BlogTeaser from "@/components/landing/BlogTeaser";
import Faq, { faqs } from "@/components/landing/Faq";
import Referral from "@/components/landing/Referral";
import Contact from "@/components/landing/Contact";
import { useLang, useTr, langMeta, HOME_PATHS } from "@/i18n";

const HREFLANG_ALTERNATES = [
  { hreflang: "it", path: HOME_PATHS.it },
  { hreflang: "en", path: HOME_PATHS.en },
  { hreflang: "de", path: HOME_PATHS.de },
  { hreflang: "x-default", path: HOME_PATHS.it },
];

const introLinks = [
  { to: "/dj-matrimoni-lugano", label: "DJ per matrimoni a Lugano" },
  { to: "/dj-matrimoni-ticino", label: "DJ per matrimoni in Ticino" },
  { to: "/dj-matrimoni-lago-di-como", label: "DJ matrimoni Lago di Como" },
  { to: "/dj-eventi-aziendali-svizzera", label: "Eventi aziendali in Svizzera" },
  { to: "/dj-compleanni-feste-private", label: "Compleanni e feste private" },
  { to: "/dj-festival-party-stagionali", label: "Feste di paese e festival" },
];

export default function LandingPage() {
  const lang = useLang();
  const tr = useTr();
  const meta = langMeta[lang] || langMeta.it;
  const faqItems = tr("faq.items", faqs);
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: lang,
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const introLabels = tr("intro.links", introLinks.map((l) => l.label));
  const links = introLinks.map((l, i) => ({ ...l, label: introLabels[i] || l.label }));
  const bullets = tr("intro.bullets", [
    "Audio e luci professionali fino a 1000 ospiti",
    "Scaletta musicale costruita su misura",
    "Sopralluogo gratuito della location",
    "Microfoni wireless per discorsi e cerimonia",
  ]);
  return (
    <>
      <Seo
        title={meta.title}
        description={meta.description}
        path={HOME_PATHS[lang] || "/"}
        jsonLd={faqJsonLd}
        lang={lang}
        alternates={HREFLANG_ALTERNATES}
      />
      <Hero />
      <StatsBar />
      <MixPlayer />

      <section className="py-20 bg-[#0B0B0B]">
        <motion.div
          className="max-w-4xl mx-auto px-6 text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight gold-text inline-block">
            {tr("intro.title", "Il DJ per il Tuo Evento in Svizzera")}
          </h2>
          <p className="text-base text-[#E2C882]/85 leading-relaxed mt-6">
            {tr("intro.p1", "Organizzi un matrimonio sul Lago di Lugano? Una festa di paese in Ticino, un compleanno speciale o una serata aziendale? Con Magix hai un unico professionista per musica, audio e luci. Da 25 anni faccio ballare la Svizzera italiana, con oltre 1500 eventi alle spalle.")}
          </p>
          <p className="text-base text-[#E2C882]/85 leading-relaxed mt-4">
            {tr("intro.p2", "Il repertorio spazia dal revival anni 80/90 alla disco dance, dall'afro house alla techno. La scaletta la costruiamo insieme, settimane prima della data. E se serve, vengo a vedere la location in anticipo.")}
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 max-w-xl mx-auto text-left">
            {bullets.map((item) => (
              <li key={item} className="flex items-start gap-2.5 text-sm text-[#E2C882]/85">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#E2B857]" />
                {item}
              </li>
            ))}
          </ul>
          <p className="text-base text-[#E2C882]/85 leading-relaxed mt-6">
            {tr("intro.p3", "Lavoro in tutto il Ticino e arrivo fino a Zurigo, Ginevra, Berna e Basilea. Copro anche il Nord Italia: Como, Milano e Varese. I prezzi partono da CHF 150. Il preventivo è gratuito e senza impegno.")}
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-7">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="text-sm text-[#E2B857] underline underline-offset-4 decoration-[rgba(226,184,87,0.4)] hover:text-[#F7DE8B] hover:decoration-[#F7DE8B] transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </motion.div>
      </section>

      <Services />
      <Genres />
      <About />
      <Gallery />
      <VideoSection />
      <Testimonials />
      <CaseStudies />
      <BlogTeaser />
      <Faq />
      <Referral />
      <Contact />
    </>
  );
}
